import React, { useEffect } from "react";
import { Redirect, Route, Switch } from "react-router-dom";

import { renderApplicationsRoutes } from "cortex/ui/src/fleet/routes/Applications";
import { renderDeviceRoutes } from "cortex/ui/src/fleet/routes/Devices";
import { renderDiagnosticRoutes } from "cortex/ui/src/fleet/routes/Diagnostics";
import { renderNetworkRoutes } from "cortex/ui/src/fleet/routes/Networks";
import { renderStrategyRoutes } from "cortex/ui/src/fleet/routes/Strategy";
import { renderUserRoutes } from "cortex/ui/src/fleet/routes/Users";
import OverviewPage from "cortex/ui/src/fleet/view/overview/Overview";
import OverviewV2Page from "cortex/ui/src/fleet/view/overview/OverviewV2";
import { FleetSideBar } from "cortex/ui/src/fleet/view/sidebar";
import {
  useAppDispatch,
  useAppSelector,
  useFeatureFlag,
} from "cortex/ui/src/hooks";
import { Pendo } from "cortex/ui/src/integrations/pendo";
import { Navigation } from "cortex/ui/src/navigation/Navigation";
import {
  authSelector,
  authStoresLoadedSelector,
  getAuthUser,
  isAuthenticated,
} from "cortex/ui/src/redux-store/auth/selectors";
import {
  DEVICES_LAST_SEEN_POLL_START,
  DEVICES_LAST_SEEN_POLL_STOP,
} from "cortex/ui/src/redux-store/last-seen-devices/actions";
import { renderAuthRoutes } from "cortex/ui/src/routes/Auth";
import { Provider } from "cortex/ui/src/ui/fetch-options";
import { FullScreenView } from "cortex/ui/src/ui/fullscreen-view";
import { BasicLoader } from "cortex/ui/src/ui/loader";
import Messages from "cortex/ui/src/ui/messages";
import { CloseSidebarOnRouteChange } from "cortex/ui/src/ui/sidebar";
import Settings from "cortex/ui/src/view/account/Settings";
import Error404View from "cortex/ui/src/view/error";
import { RedirectView } from "cortex/ui/src/view/redirect";

import Varz from "./Varz";

export default function Main() {
  const isOverviewDashboardEnabled = useFeatureFlag(
    "is-overview-dashboard-enabled",
  );
  const dispatch = useAppDispatch();
  const auth = useAppSelector(authSelector);
  const userIsAuthenticated = isAuthenticated(auth);
  const storesLoaded = useAppSelector(authStoresLoadedSelector);
  const user = useAppSelector(getAuthUser);

  useEffect(() => {
    if (!userIsAuthenticated) {
      return undefined;
    }

    dispatch({ type: DEVICES_LAST_SEEN_POLL_START });

    return () => {
      dispatch({ type: DEVICES_LAST_SEEN_POLL_STOP });
    };
  }, [dispatch, userIsAuthenticated]);

  if (!storesLoaded) {
    return <BasicLoader>Cortex Fleet Loading...</BasicLoader>;
  }

  const renderRoutes = () => {
    if (userIsAuthenticated) {
      return (
        <Switch>
          {renderApplicationsRoutes()}
          {renderDeviceRoutes()}
          {renderDiagnosticRoutes()}
          {renderNetworkRoutes()}
          {renderStrategyRoutes()}
          {renderUserRoutes()}
          <Route exact path="/" render={() => <Redirect to="/overview" />} />
          <Route
            exact
            path="/login"
            render={() => <Redirect to="/overview" />}
          />
          <Route
            component={
              isOverviewDashboardEnabled ? OverviewV2Page : OverviewPage
            }
            exact
            path="/overview"
          />
          <Route component={Settings} exact path="/settings" />
          <Route component={Varz} exact path="/varz" />
          <Route component={RedirectView} path="/redirect" />
          <Route component={Error404View} />
        </Switch>
      );
    }

    // Return default logged out routes with a redirection to login by default
    // instead of a 404.
    return (
      <Switch>
        {renderAuthRoutes()}
        <Route render={() => <Redirect to="/login" />} />
      </Switch>
    );
  };

  return (
    <>
      <Pendo user={user} />
      <CloseSidebarOnRouteChange />
      <FullScreenView />
      <Messages />
      {userIsAuthenticated && user ? (
        <div>
          <Provider.PlayerVersionOptions />
          <Navigation user={user} />
          {renderRoutes()}
        </div>
      ) : (
        renderRoutes()
      )}
      <FleetSideBar />
    </>
  );
}
